import React, { useState, useEffect } from 'react';
import { AlertCircle, Calendar, DollarSign, Clock, FileText } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  cancellation: Calendar,
  reschedule: Clock,
  refund: DollarSign,
  contact_info: FileText
};

const formatContent = (content) => {
  if (!content) return null;
  return content.split('\n\n').map((block, i) => {
    const lines = block.split('\n');
    const heading = lines[0].endsWith(':') ? lines[0] : null;
    const text = heading ? lines.slice(1).join('\n') : block;
    return (
      <div key={i} className={i > 0 ? 'mt-4' : ''}>
        {heading && <h4 className="font-semibold text-gray-900 mb-1">{heading}</h4>}
        <p className="text-gray-600 whitespace-pre-line">{text}</p>
      </div>
    );
  });
};

export const Policy = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/policies`)
      .then(r => setPolicies(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen">
      <section className="bg-gradient-to-r from-red-50 to-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Cancellation, Reschedule & <span className="text-red-600">Refund Policy</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            Please review our policies carefully before booking your training class.
          </p>
        </div>
      </section>

      <section className="py-8 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Alert className="border-2 border-red-200 bg-red-50">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <AlertTitle className="text-red-900 font-semibold">Important</AlertTitle>
            <AlertDescription className="text-red-800">
              By booking a class with PUSH Training - NC, you agree to the following policies. Please read them carefully.
            </AlertDescription>
          </Alert>
        </div>
      </section>

      <section className="py-8 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          {loading && (
            <div className="text-center py-8 text-gray-400">Loading policies...</div>
          )}
          {policies.map((policy) => {
            const Icon = ICONS[policy.id] || FileText;
            return (
              <Card key={policy.id} className="border-2">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                      <Icon className="text-red-600" size={24} />
                    </div>
                    <CardTitle className="text-2xl">{policy.title}</CardTitle>
                  </div>
                  {policy.description && (
                    <CardDescription className="text-base">{policy.description}</CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  {formatContent(policy.content)}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      <section className="py-8 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-500">
            PUSH Training - NC reserves the right to modify these policies at any time. Changes will be posted on this page.
          </p>
        </div>
      </section>
    </div>
  );
};
